/**
 * Regras de redirecionamento da autenticacao, compartilhadas por web e admin.
 *
 * Servidor e navegador precisam tomar a MESMA decisao sobre um `redirectTo`,
 * senao um lado aceita o destino que o outro recusa. Por isso a validacao mora
 * aqui e nao em cada app.
 */
export const DEFAULT_AUTHENTICATED_PATH = "/account";
export const LOGIN_PATH = "/auth/login";
export const VERIFY_EMAIL_PATH = "/auth/verify-email";
export const SECOND_FACTOR_PATH = "/auth/second-factor";
export const REFRESH_SESSION_PATH = "/auth/refresh-session";
export const REDIRECT_QUERY_PARAM = "redirectTo";
/**
 * Cookie curto gravado pela pagina de refresh logo antes de voltar ao destino.
 * Se o SSR ainda recusar a sessao enquanto ele existe, o refresh "funcionou" mas
 * nao resolveu — e mandar de novo a pagina de refresh criaria um laco. Nesse
 * caso o guard vai direto ao login.
 */
export const REFRESH_ATTEMPT_COOKIE = "st-refresh-attempted";
export const REFRESH_ATTEMPT_MAX_AGE_SECONDS = 30;

/** Header que o `proxy.ts` preenche com o path atual para o SSR montar o retorno. */
export const CURRENT_PATH_HEADER = "x-geooffice-current-path";

const PLACEHOLDER_ORIGIN = "https://geooffice.local";

/** Telas de autenticacao para as quais voltar criaria um laco. */
const LOOP_PATHS = [LOGIN_PATH, REFRESH_SESSION_PATH, SECOND_FACTOR_PATH];

function hasControlCharacters(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

function isLoopPath(pathname: string): boolean {
  return LOOP_PATHS.includes(pathname);
}

/**
 * Normaliza uma lista de origens (`https://a.com, http://localhost:9001`) para
 * o formato de `URL.origin`. Entradas invalidas ou com path sao descartadas —
 * a allowlist compara origem exata, nunca sufixo.
 */
export function parseOriginList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .flatMap((entry) => {
      try {
        const url = new URL(entry);
        if (url.protocol !== "http:" && url.protocol !== "https:") return [];
        return [url.origin];
      } catch {
        return [];
      }
    });
}

/**
 * Aceita apenas paths que ficam nesta aplicacao.
 *
 * Alem de impedir open redirect, manter esta funcao compartilhada entre
 * servidor e navegador garante que os dois lados decidam igual.
 */
export function getSafeRedirectPath(
  value: string | null | undefined,
  fallback = DEFAULT_AUTHENTICATED_PATH,
): string {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    hasControlCharacters(value)
  ) {
    return fallback;
  }

  try {
    const url = new URL(value, PLACEHOLDER_ORIGIN);

    if (url.origin !== PLACEHOLDER_ORIGIN) {
      return fallback;
    }

    if (isLoopPath(url.pathname)) {
      return fallback;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

/**
 * Como `getSafeRedirectPath`, mas tambem aceita URL absoluta cuja origem esteja
 * na allowlist. E o que permite ao login central do web devolver o usuario ao
 * admin, que vive em outro subdominio (ou outra porta, localmente).
 */
export function getSafeRedirectTarget(
  value: string | null | undefined,
  options: { allowedOrigins?: readonly string[]; fallback?: string } = {},
): string {
  const { allowedOrigins = [], fallback = DEFAULT_AUTHENTICATED_PATH } =
    options;

  if (!value) return fallback;
  if (value.startsWith("/")) return getSafeRedirectPath(value, fallback);
  if (value.includes("\\") || hasControlCharacters(value)) return fallback;

  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return fallback;
    if (url.username || url.password) return fallback;
    if (!allowedOrigins.includes(url.origin)) return fallback;
    if (isLoopPath(url.pathname)) return fallback;
    return url.toString();
  } catch {
    return fallback;
  }
}

function withRedirectParam(basePath: string, redirectTo: string): string {
  const params = new URLSearchParams({ [REDIRECT_QUERY_PARAM]: redirectTo });
  return `${basePath}?${params.toString()}`;
}

export function buildLoginPath(
  redirectTo?: string | null,
  allowedOrigins: readonly string[] = [],
): string {
  const safeRedirect = getSafeRedirectTarget(redirectTo, {
    allowedOrigins,
    fallback: "",
  });

  return safeRedirect ? withRedirectParam(LOGIN_PATH, safeRedirect) : LOGIN_PATH;
}

export function buildVerifyEmailPath(
  redirectTo?: string | null,
  allowedOrigins: readonly string[] = [],
): string {
  return withRedirectParam(
    VERIFY_EMAIL_PATH,
    getSafeRedirectTarget(redirectTo, { allowedOrigins }),
  );
}

/** Tela do codigo enviado por e-mail (segundo fator), voltando a `redirectTo`. */
export function buildSecondFactorPath(
  redirectTo?: string | null,
  allowedOrigins: readonly string[] = [],
): string {
  return withRedirectParam(
    SECOND_FACTOR_PATH,
    getSafeRedirectTarget(redirectTo, { allowedOrigins }),
  );
}

/**
 * Pagina que tenta renovar a sessao no navegador antes de mandar ao login. O
 * SSR nao consegue renovar sozinho: o refresh token so vale para `/api/auth`
 * e precisa do SDK do cliente para gravar os cookies novos.
 */
export function buildRefreshSessionPath(redirectTo?: string | null): string {
  return withRedirectParam(REFRESH_SESSION_PATH, getSafeRedirectPath(redirectTo));
}

/**
 * Destino de um SSR que recebeu 401: a pagina de refresh na primeira vez, o
 * login se o refresh acabou de acontecer e nao adiantou.
 */
export function buildUnauthorisedRedirectPath(
  redirectTo: string | null | undefined,
  refreshAlreadyAttempted: boolean,
): string {
  return refreshAlreadyAttempted
    ? buildLoginPath(redirectTo)
    : buildRefreshSessionPath(redirectTo);
}

/**
 * O backend recusou a sessao. SuperTokens responde 401 tanto para "access token
 * vencido, tente refresh" quanto para "sem sessao"; os dois casos passam pela
 * pagina de refresh, que e quem sabe distinguir.
 */
export function isUnauthorisedError(
  error: { status?: number } | null | undefined,
): boolean {
  return error?.status === 401;
}
